import { Tooltip } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { useMemo, useRef, useState } from 'react';
import ReactHotkeys from 'react-hot-keys';
import styled, { css } from 'styled-components';

import { MenuArrowDownSvg } from '../../../../assets/icons';
import EnterprisePlanIcon from '../../../../assets/icons/EnterprisePlanIcon';
import theme from '../../../../assets/theme';
import { LicenseCoarseLevels } from '../../../../hooks/useAccessBadge';
import { useOnOutsideClick } from '../../../../hooks/useOnOutsideClick';
import usePaywallPrompt from '../../../../hooks/usePaywallPrompt';
import { PromptDropdownOption } from '../../../../services/backendService/types';
import { IconWrapper } from '../../../_atoms/IconWrapper';

export const GeneratorDropdown = ({
  label,
  onSelect,
  options,
  value,
}: {
  label: string;
  value: string | undefined;
  options: PromptDropdownOption[];
  onSelect: (value: string) => void;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const { isReady, promptPaywall, paywallComponent, userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });

  const handleEscapePress = () => {
    setShowDropdown(false);
  };

  const handleSelect = (value: string) => {
    const option = options.find(o => o.value === value);
    if (
      !option?.isEnterprise ||
      (option?.isEnterprise &&
        isReady &&
        userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise)
    ) {
      // Go on with the normal flow
      setShowDropdown(false);
      onSelect(value);
      return;
    }
    if (
      option?.isEnterprise &&
      isReady &&
      userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
    ) {
      promptPaywall({ label: 'Upgrade account' });
      return;
    }
  };

  const selectedOption = useMemo(() => {
    return options.find(o => o.value === value);
  }, [options, value]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnOutsideClick(wrapperRef, handleEscapePress);

  return (
    <Wrapper ref={wrapperRef}>
      {paywallComponent}
      {label && <Label>{label}</Label>}
      <FakeInput onClick={() => setShowDropdown(!showDropdown)}>
        <span style={{ flex: 1 }}>{selectedOption?.displayName}</span>
        <IconWrapper height="12px" width="12px">
          <MenuArrowDownSvg />
        </IconWrapper>
      </FakeInput>
      <div style={{ position: 'relative', width: '100%' }}>
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 2,
                width: '100%',
                left: 0,
                background: 'white',
                zIndex: 9999,
                boxShadow: theme.effects.defaultShadow,
                borderRadius: 5,
                maxHeight: 250,
                overflowY: 'auto',
              }}
            >
              {options.map(option => (
                <OptionContainer
                  key={option.value}
                  $selected={option.value === value}
                  $isEnterprise={option.isEnterprise}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.displayName}
                  {option.isEnterprise && (
                    <Tooltip
                      title={
                        userAccessBadge?.license.coarseLevel ===
                        LicenseCoarseLevels.enterprise
                          ? "Available to you as you're on the school plan"
                          : 'Available on the school plan'
                      }
                    >
                      <IconWrapper
                        height="16px"
                        width="16px"
                        style={{
                          position: 'absolute',
                          paddingBottom: 2,
                          right: 10,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color:
                            userAccessBadge?.license.coarseLevel ===
                            LicenseCoarseLevels.enterprise
                              ? theme.colors.black
                              : theme.colors.yellow,
                        }}
                      >
                        <EnterprisePlanIcon />
                      </IconWrapper>
                    </Tooltip>
                  )}
                </OptionContainer>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ReactHotkeys keyName="Escape" onKeyUp={handleEscapePress} />
    </Wrapper>
  );
};

export const GeneratorDropdownV2 = ({
  label,
  onSelect,
  options,
  value,
}: {
  label: string;
  value: string | undefined;
  options: PromptDropdownOption[];
  onSelect: (value: string) => void;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const { isReady, promptPaywall, paywallComponent, userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });

  const handleEscapePress = () => {
    setShowDropdown(false);
  };

  const handleSelect = (value: string) => {
    const option = options.find(o => o.value === value);
    if (
      !option?.isEnterprise ||
      (option?.isEnterprise &&
        isReady &&
        userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise)
    ) {
      // Go on with the normal flow
      setShowDropdown(false);
      onSelect(value);
      return;
    }

    if (
      option?.isEnterprise &&
      isReady &&
      userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
    ) {
      promptPaywall({
        label: 'Generator Dropdown',
        description: `${selectedOption?.displayName}`,
      });
      return;
    }
  };

  const selectedOption = useMemo(() => {
    return options.find(o => o.value === value);
  }, [options, value]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnOutsideClick(wrapperRef, handleEscapePress);

  return (
    <Wrapper ref={wrapperRef}>
      {paywallComponent}
      {label && <LabelV2>{label}</LabelV2>}
      <FakeInputV2 onClick={() => setShowDropdown(!showDropdown)}>
        <span style={{ flex: 1 }}>{selectedOption?.displayName}</span>
        <IconWrapper height="12px" width="12px">
          <MenuArrowDownSvg />
        </IconWrapper>
      </FakeInputV2>
      <div style={{ position: 'relative', width: '100%' }}>
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 2,
                width: '100%',
                left: 0,
                background: 'white',
                zIndex: 9999,
                boxShadow: theme.effects.defaultShadow,
                borderRadius: 5,
                maxHeight: 250,
                overflowY: 'auto',
              }}
            >
              {options.map(option => (
                <OptionContainer
                  key={option.value}
                  $selected={option.value === value}
                  $isEnterprise={option.isEnterprise}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.displayName}
                  {option.isEnterprise && (
                    <Tooltip
                      title={
                        userAccessBadge?.license.coarseLevel ===
                        LicenseCoarseLevels.enterprise
                          ? "Available to you as you're on the school plan"
                          : 'Available on the school plan'
                      }
                    >
                      <IconWrapper
                        height="16px"
                        width="16px"
                        style={{
                          position: 'absolute',
                          paddingBottom: 2,
                          right: 10,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color:
                            userAccessBadge?.license.coarseLevel ===
                            LicenseCoarseLevels.enterprise
                              ? theme.colors.black
                              : theme.colors.yellow,
                        }}
                      >
                        <EnterprisePlanIcon />
                      </IconWrapper>
                    </Tooltip>
                  )}
                </OptionContainer>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ReactHotkeys keyName="Escape" onKeyUp={handleEscapePress} />
    </Wrapper>
  );
};

const OptionContainer = styled.div<{ $selected?: boolean; $isEnterprise?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    background: ${theme.colors.greyLight};
  }
  user-select: none;

  ${({ $selected }) =>
    $selected &&
    css`
      background: #f6ece0;
      font-weight: bold;
    `}
  ${({ $isEnterprise }) =>
    $isEnterprise &&
    css`
      padding: 10px 35px 10px 15px;
    `}
`;

const FakeInput = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  line-height: 22px;
  font-size: 16px;
  outline: none;
  border: 1px solid rgb(217, 217, 217);
  border-radius: 5px;
  padding: 10px 15px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover,
  &:focus {
    border: 1px solid ${theme.colors.green};
  }
`;

const FakeInputV2 = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  line-height: 22px;
  font-size: 16px;
  outline: none;
  border: 1px solid rgb(217, 217, 217);
  border-radius: 5px;
  padding: 10px 15px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  background: transparent;
  color: white;
  &:hover,
  &:focus {
    border: 1px solid white;
  }
`;

const Label = styled.div`
  color: ${theme.colors.black};
  font-family: 'Nunito';
  font-style: normal;
  font-weight: bold;
  font-size: 12px;
  line-height: 18px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
`;

const LabelV2 = styled.div`
  color: #fff;

  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Nunito;
  font-size: 18px;
  font-style: normal;
  font-weight: 800;
  line-height: 140%;
`;

const Wrapper = styled.div<{ $fluid?: boolean }>`
  cursor: pointer;
  position: relative;
`;
